import { useOnboarding } from "@/hooks/use-onboarding";
import { mediumImpact } from "@/utils/capacitorUtils";
import { IonContent, IonPage } from "@ionic/react";
import { motion } from "framer-motion";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";

interface MotivationSelectionProps {
  nextStep: string;
  setLoadingState?: (loading: boolean, hideHeader?: boolean) => void;
}

const MotivationSelection: React.FC<MotivationSelectionProps> = ({ nextStep, setLoadingState }) => {
  const { t } = useTranslation("MGP");
  const history = useHistory();
  const { updateOnboardingData } = useOnboarding()

  const [selectedMotivation, setSelectedMotivation] = useState<string | null>(null);

  // Motivation options list
  const motivations = [
    { id: "learn_basics", icon: "🔰", name: t("learn_basics"), description: t("learn_basics_desc") },
    { id: "improve_swing", icon: "🏌️", name: t("improve_swing"), description: t("improve_swing_desc") },
    { id: "lower_handicap", icon: "📉", name: t("lower_handicap"), description: t("lower_handicap_desc") },
    { id: "track_stats", icon: "📊", name: t("track_stats"), description: t("track_stats_desc") },
    { id: "find_courses", icon: "🗺️", name: t("find_courses"), description: t("find_courses_desc") },
    { id: "casual_fun", icon: "🌴", name: t("casual_fun"), description: t("casual_fun_desc") },
    { id: "connect_golfers", icon: "🤝", name: t("connect_golfers"), description: t("connect_golfers_desc") },
    { id: "stay_active", icon: "💪", name: t("stay_active"), description: t("stay_active_desc") },
  ];

  const handleNext = async (motivation: string) => {
    try {
      // Notify parent component about loading state
      setLoadingState?.(true);
      mediumImpact();
      
      await updateOnboardingData({ motivation });
      setSelectedMotivation(motivation);
      
      // Reset loading state before navigation
      setLoadingState?.(false);
      history.push(nextStep);
    } catch (error) {
      console.error("Error updating motivation:", error);
      // Reset loading state on error
      setLoadingState?.(false);
    }
  };

  return (
    <IonPage>
      <IonContent scrollY={false}>
        <div
          className="flex flex-col px-2 bg-card h-full"
        >
          <div className="w-full max-w-2xl mx-auto">
            <h1 className="mb-4 text-xl font-bold">{t("what_motivates_you_to_play")}</h1>
            {/* Vertical List of Motivations */}
            <div className="w-full space-y-4">
              {motivations.map((motivation) => (
                <motion.div
                  key={motivation.id}
                  className={`flex items-center p-4 rounded-lg bg-card hover:bg-input cursor-pointer shadow-sm border ${selectedMotivation === motivation.id
                    ? "border-primary bg-primary/10"
                    : "border-input"
                    }`}
                  onClick={() => { setSelectedMotivation(motivation.id); handleNext(motivation.id); }}
                >
                  <div className="text-4xl mr-4">{motivation.icon}</div>
                  <div>
                    <div className="font-medium text-base">{motivation.name}</div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </IonContent>
    </IonPage>
  );
};

export default MotivationSelection;